<template>
  <div class="pa-3">
    <v-row>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="model.name"
          label="Nome*"
          :rules="[rules.required]"
          :readonly="false"
          :disabled="loader.loading"
          variant="outlined"
        />
      </v-col>
      <v-col cols="12" md="6">
        <v-select
          v-model="model.type"
          label="Tipo de Evento*"
          :items="[
            { title: 'Eventos Esportivos', value: 1 },
            { title: 'Eventos Sociais', value: 2 }
          ]"
          item-text="title"
          item-value="value"
          :rules="[rules.required]"
          :readonly="false"
          :disabled="loader.loading"
          variant="outlined"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12" sm="6">
        <v-select
          v-model="locationId"
          label="Local*"
          :items="locationInputItems"
          item-text="title"
          item-value="value"
          :rules="[rules.required]"
          :readonly="false"
          :disabled="editing || loader.loading"
          :loading="loader.loading"
          variant="outlined"
        />
      </v-col>
      <v-col cols="12" sm="6">
        <v-select
          v-model="model.mapping"
          label="Mapeamento*"
          :items="mappingInputItems"
          item-title="name"
          return-object
          :rules="[rules.required]"
          :readonly="false"
          :disabled="editing || !locationId || loader.loading"
          :loading="loader.loading"
          variant="outlined"
        />
      </v-col>
    </v-row>
    <v-row>
      <v-col
        cols="12"
        sm="6"
        md="3"
        class="d-flex ga-2"
      >
        <v-text-field
          v-model="model.issuer"
          label="Órgão Emitente*"
          :rules="[rules.required]"
          :readonly="false"
          :disabled="loader.loading"
          variant="outlined"
        />
        <t-tooltip location="top" class="mt-4">
          Órgão emitente é a entidade responsável pela organização do evento.
        </t-tooltip>
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-select
          v-model="model.template"
          label="Template de Impressão*"
          :items="printingTemplates"
          :rules="[rules.required]"
          :readonly="false"
          :disabled="loader.loading"
          variant="outlined"
        />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-select
          v-model="model.template_type"
          label="Template do Voucher*"
          :items="voucherTemplates"
          :rules="[rules.required]"
          :readonly="false"
          :disabled="loader.loading"
          variant="outlined"
        />
      </v-col>
      <v-col cols="12" sm="6" md="3">
        <v-select
          v-model="model.active"
          label="Status*"
          :items="[
            { title: 'Ativo', value: true },
            { title: 'Inativo', value: false },
          ]"
          :readonly="false"
          :disabled="loader.loading"
          variant="outlined"
        />
      </v-col>
    </v-row>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, watch } from 'vue'
import { PrintingTemplateLabels } from '@/constants/PrintingTemplates'
import { VoucherTemplateLabels } from '@/constants/VoucherTemplates'
import { FreeTypes } from '@/constants/TicketTypeNames'
import { rules } from '@/helpers/rules'
import MappingService from '@/services/MappingService'
import { useLoader } from '@/stores/loader'
import AdditionalField from '@/types/AdditionalField'
import EventConfiguration from '@/types/EventConfiguration'
import Local from '@/types/Local'
import Mapping from '@/types/Mapping'
import TicketType from '@/types/TicketType'
import TTooltip from '@/components/TTooltip.vue'

const loader = useLoader()

const model = defineModel<EventConfiguration>({ required: true })

const { editing } = defineProps<{ editing: boolean }>()

const printingTemplates = Object.entries(PrintingTemplateLabels).map(([value, title]) => ({ title, value }))

const voucherTemplates = Object.entries(VoucherTemplateLabels).map(([value, title]) => ({ title, value }))

const mappings = ref<Mapping[]>([])

const mappingInputItems = computed(() => mappings.value.filter(m => m.local?.id === locationId.value))

const locationId = ref<string | undefined>()

const locationInputItems = computed(() => {
  const locations: Local[] = []
  mappings.value.forEach((mapping) => {
    if (mapping.local && !locations.some(location => location.id === mapping.local?.id)) {
      locations.push(mapping.local)
    }
  })
  return locations
    .sort((a, b) => a.name.localeCompare(b.name))
    .map(l => ({ title: `${l.name} - ${l.id.split('-')[0]}`, value: l.id }))
})

async function fetchMappings() {
  try {
    loader.increment()
    const response = await (new MappingService()).index(1, 'status=1', 50)
    mappings.value = response.data.data

    if (model.value.mapping && !mappings.value.find(m => m.id === model.value.mapping?.id)) {
      mappings.value.push(model.value.mapping)
    }
  } finally {
    loader.decrement()
  }
}

watch(
  () => model.value.mapping,
  () => {
    model.value.mapping?.sectors?.forEach((sector) => {
      sector.sections.forEach((section) => {
        section.ticket_types = FreeTypes.map(name => ({
          name: name,
          price: 0,
          passport_amount: 1,
          max_installments: 1,
          section_capacity: sector.capacity, // lol
          section_id: section.id,
          section_name: section.name,
          sector_id: sector.id,
          sector_name: sector.name,
          additional_fields: [] as AdditionalField[],
        } as TicketType))
      })
    })
  },
)

onMounted(() => {
  fetchMappings()
  if (editing) {
    locationId.value = model.value.mapping?.local?.id
  }
})

</script>
